import * as React from "react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";

import { cn } from "@shared/lib/utils";

/**
 * ScrollArea de shadcn, avec un correctif de hauteur.
 *
 * Le viewport de Radix porte `size-full` (`height: 100%`). Dans une colonne
 * flex — une fiche `Dialog` avec en-tête, corps défilant et pied de page — la
 * racine tire sa hauteur de `flex: 1`, sans hauteur *déclarée* : le
 * pourcentage ne se résout donc pas et le viewport prend la hauteur de son
 * contenu. Résultat : rien ne défile, le contenu passe sous le pied de page et
 * sort de la boîte de dialogue (fiche lead, onglet Synthèse).
 *
 * On fait donc de la racine une colonne flex qui clippe, et du viewport son
 * enfant extensible : la hauteur vient de la répartition flex, plus d'un
 * pourcentage à résoudre. Une `ScrollArea` à hauteur explicite (`h-72`) ou
 * dictée par son contenu se comporte exactement comme avant.
 */

function ScrollArea({
  className,
  children,
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.Root>) {
  return (
    <ScrollAreaPrimitive.Root
      data-slot="scroll-area"
      className={cn("relative flex flex-col overflow-hidden", className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        data-slot="scroll-area-viewport"
        className="focus-visible:ring-ring/50 min-h-0 w-full flex-1 rounded-[inherit] transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:outline-1"
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
}

function ScrollBar({
  className,
  orientation = "vertical",
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>) {
  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      data-slot="scroll-area-scrollbar"
      orientation={orientation}
      className={cn(
        "flex touch-none p-px transition-colors select-none",
        orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent",
        orientation === "horizontal" && "h-2.5 flex-col border-t border-t-transparent",
        className,
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb
        data-slot="scroll-area-thumb"
        className="bg-border relative flex-1 rounded-full"
      />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  );
}

export { ScrollArea, ScrollBar };
